import { FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PromptSlot, PromptBlock, GameProgress, GameResult } from '@/types/promptChallenge';
import { mockPromptChallengeGame } from '@/data/promptChallengeMock';

const PromptChallengePage: FC = () => {
  const navigate = useNavigate();
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(180);
  const [slots, setSlots] = useState<PromptSlot[]>([
    { type: 'persona', label: '[PERSONA]', block: null },
    { type: 'context', label: '[CONTEXTO]', block: null },
    { type: 'task', label: '[TAREFA]', block: null },
    { type: 'restrictions', label: '[RESTRIÇÕES]', block: null },
  ]);
  const [usedBlocks, setUsedBlocks] = useState<Set<string>>(new Set());
  const [draggedBlock, setDraggedBlock] = useState<PromptBlock | null>(null);
  const [gameResult, setGameResult] = useState<GameResult | null>(null);
  const [completedChallenges, setCompletedChallenges] = useState<string[]>([]);

  const currentChallenge = mockPromptChallengeGame.challenges[currentChallengeIndex];
  const progress: GameProgress = {
    currentChallengeIndex,
    totalChallenges: mockPromptChallengeGame.challenges.length,
    completedChallenges,
    timeRemaining,
  };

  const handleTimeUp = useCallback(() => {
    const timeUsed = currentChallenge.timeLimit - timeRemaining;
    setGameResult({
      challengeId: currentChallenge.id,
      completed: true,
      timeUsed,
      isCorrect: false,
      score: 0,
      xpEarned: 0,
    });
  }, [currentChallenge, timeRemaining]);

  // Timer
  useEffect(() => {
    if (gameResult) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [gameResult, currentChallengeIndex, handleTimeUp]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDragStart = (block: PromptBlock) => {
    setDraggedBlock(block);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (slotType: string) => {
    if (!draggedBlock) return;

    // Verifica se o tipo do bloco corresponde ao slot
    if (draggedBlock.type !== slotType) {
      setDraggedBlock(null);
      return;
    }

    // Remove o bloco do slot anterior se ele já estava em algum lugar
    const newSlots = slots.map((slot) => {
      if (slot.block?.id === draggedBlock.id) {
        return { ...slot, block: null };
      }
      return slot;
    });

    // Adiciona o bloco ao novo slot
    const updatedSlots = newSlots.map((slot) => {
      if (slot.type === slotType) {
        return { ...slot, block: draggedBlock };
      }
      return slot;
    });

    setSlots(updatedSlots);
    setUsedBlocks((prev) => new Set([...prev, draggedBlock.id]));
    setDraggedBlock(null);
  };

  const handleRemoveBlock = (slotType: string) => {
    const newSlots = slots.map((slot) => {
      if (slot.type === slotType && slot.block) {
        setUsedBlocks((prev) => {
          const newSet = new Set(prev);
          newSet.delete(slot.block!.id);
          return newSet;
        });
        return { ...slot, block: null };
      }
      return slot;
    });
    setSlots(newSlots);
  };

  const handleClearAll = () => {
    setSlots([
      { type: 'persona', label: '[PERSONA]', block: null },
      { type: 'context', label: '[CONTEXTO]', block: null },
      { type: 'task', label: '[TAREFA]', block: null },
      { type: 'restrictions', label: '[RESTRIÇÕES]', block: null },
    ]);
    setUsedBlocks(new Set());
  };

  const handleTestPrompt = () => {
    const solution = currentChallenge.correctSolution;
    const isCorrect =
      slots[0].block?.id === solution.persona &&
      slots[1].block?.id === solution.context &&
      slots[2].block?.id === solution.task &&
      slots[3].block?.id === solution.restrictions;

    const timeUsed = currentChallenge.timeLimit - timeRemaining;
    const xpEarned = isCorrect ? 10 : 0;

    setGameResult({
      challengeId: currentChallenge.id,
      completed: true,
      timeUsed,
      isCorrect,
      score: isCorrect ? 100 : 0,
      xpEarned,
    });

    if (isCorrect) {
      setCompletedChallenges((prev) => [...prev, currentChallenge.id]);
    }
  };

  const handleNextChallenge = () => {
    if (currentChallengeIndex < mockPromptChallengeGame.challenges.length - 1) {
      setCurrentChallengeIndex((prev) => prev + 1);
      setTimeRemaining(mockPromptChallengeGame.challenges[currentChallengeIndex + 1].timeLimit);
      setGameResult(null);
      handleClearAll();
    } else {
      // Fim de todos os desafios
      navigate('/play');
    }
  };

  const handleRestart = () => {
    setCurrentChallengeIndex(0);
    setTimeRemaining(mockPromptChallengeGame.challenges[0].timeLimit);
    setCompletedChallenges([]);
    setGameResult(null);
    handleClearAll();
  };

  const handleExit = () => {
    navigate('/play');
  };

  // Tela de resultado
  if (gameResult) {
    return (
      <div className="min-h-screen bg-surface-primary text-content-primary flex flex-col">
        <header className="flex items-center justify-between border-b border-slate-800 px-8 py-4 bg-surface-primary/80 backdrop-blur-md">
          <div className="flex items-center gap-3 text-accent">
            <div className="size-10 flex items-center justify-center bg-accent/10 rounded-xl">
              <span className="material-symbols-outlined text-accent text-2xl">terminal</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Prompt Challenge</h1>
              <p className="text-2xs uppercase tracking-widest text-slate-500 font-bold">
                {currentChallenge.objective.difficulty}
              </p>
            </div>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-2xl">
            <div
              className={`relative bg-slate-900 border ${gameResult.isCorrect ? 'border-green-500' : 'border-red-500'} rounded-xl p-8 md:p-12 shadow-2xl`}
            >
              <div className="flex justify-center mb-6">
                <div
                  className={`size-24 rounded-full flex items-center justify-center ${gameResult.isCorrect ? 'bg-green-500' : 'bg-red-500'}`}
                >
                  <span className="material-symbols-outlined text-white text-5xl">
                    {gameResult.isCorrect ? 'check_circle' : 'cancel'}
                  </span>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-center mb-4">
                {gameResult.isCorrect ? 'Prompt Correto! 🎉' : 'Prompt Incorreto'}
              </h2>

              <p className="text-center text-slate-400 mb-8">
                {gameResult.isCorrect
                  ? 'Você montou o prompt estruturado corretamente!'
                  : 'Revise a estrutura do prompt e tente novamente.'}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-accent">{formatTime(gameResult.timeUsed)}</p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">Tempo Usado</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-2xl font-bold text-accent">
                    {gameResult.isCorrect ? `+${gameResult.xpEarned}` : '0'}
                  </p>
                  <p className="text-xs text-slate-400 uppercase tracking-wider">XP Ganho</p>
                </div>
              </div>

              <div className="flex gap-4">
                {gameResult.isCorrect ? (
                  <>
                    {currentChallengeIndex < mockPromptChallengeGame.challenges.length - 1 ? (
                      <button
                        onClick={handleNextChallenge}
                        className="flex-1 py-3 bg-accent text-accent-contrast rounded-xl font-bold hover:brightness-110 transition-all"
                      >
                        Próximo Desafio
                      </button>
                    ) : (
                      <button
                        onClick={handleExit}
                        className="flex-1 py-3 bg-accent text-accent-contrast rounded-xl font-bold hover:brightness-110 transition-all"
                      >
                        Concluir
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setGameResult(null);
                        setTimeRemaining(currentChallenge.timeLimit);
                        handleClearAll();
                      }}
                      className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 rounded-xl font-bold transition-all"
                    >
                      Tentar Novamente
                    </button>
                    <button
                      onClick={handleExit}
                      className="flex-1 py-3 bg-accent text-accent-contrast rounded-xl font-bold hover:brightness-110 transition-all"
                    >
                      Voltar ao Hub
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Tela do jogo
  return (
    <div className="min-h-screen bg-surface-primary text-content-primary flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-800 px-8 py-4 bg-surface-primary/80 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 text-accent">
            <div className="size-10 flex items-center justify-center bg-accent/10 rounded-xl">
              <span className="material-symbols-outlined text-accent text-2xl">terminal</span>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight">Prompt Challenge</h1>
              <p className="text-2xs uppercase tracking-widest text-slate-500 font-bold">
                {currentChallenge.objective.difficulty}
              </p>
            </div>
          </div>
          <div className="h-8 w-px bg-slate-800"></div>
          <div className="hidden md:flex flex-col">
            <span className="text-2xs text-slate-500 uppercase font-bold tracking-wider">
              Objetivo do Desafio
            </span>
            <h2 className="text-lg font-medium text-accent-cyan">{currentChallenge.objective.title}</h2>
          </div>
        </div>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3 bg-slate-900/50 px-4 py-2 rounded-xl border border-slate-800">
            <span className="material-symbols-outlined text-yellow-500">timer</span>
            <span className="font-mono font-bold text-lg">{formatTime(timeRemaining)}</span>
          </div>
          <button
            onClick={handleExit}
            className="size-10 rounded-full border-2 border-accent overflow-hidden hover:opacity-80 transition-opacity"
          >
            <div className="w-full h-full bg-slate-700 flex items-center justify-center">
              <span className="material-symbols-outlined text-white">close</span>
            </div>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 border-r border-slate-800 p-6 flex flex-col bg-surface-primary/50">
          <div className="mb-8">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">Progresso</h3>
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-black text-content-primary">
                {currentChallengeIndex + 1}{' '}
                <span className="text-slate-600 text-lg">de {progress.totalChallenges}</span>
              </span>
              <span className="text-accent font-bold text-sm">
                {Math.round(((currentChallengeIndex + 1) / progress.totalChallenges) * 100)}%
              </span>
            </div>
            <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden">
              <div
                className="bg-accent h-full shadow-accent/50"
                style={{
                  width: `${((currentChallengeIndex + 1) / progress.totalChallenges) * 100}%`,
                }}
              ></div>
            </div>
          </div>

          <div className="space-y-3">
            {mockPromptChallengeGame.challenges.map((challenge, index) => (
              <div
                key={challenge.id}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  completedChallenges.includes(challenge.id)
                    ? 'bg-accent/10 border border-accent/30'
                    : challenge.isLocked
                      ? 'bg-slate-900/50 border border-slate-800 text-slate-500'
                      : index === currentChallengeIndex
                        ? 'bg-accent-cyan/10 border border-accent-cyan/30'
                        : 'bg-slate-900/50 border border-slate-800'
                }`}
              >
                <span className="material-symbols-outlined text-sm">
                  {completedChallenges.includes(challenge.id)
                    ? 'check_circle'
                    : challenge.isLocked
                      ? 'lock'
                      : 'radio_button_unchecked'}
                </span>
                <span className="text-sm font-medium">
                  Desafio {challenge.number}: {challenge.title}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-auto">
            <button
              onClick={handleRestart}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors font-bold text-sm"
            >
              <span className="material-symbols-outlined text-sm">restart_alt</span>
              Reiniciar
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col p-8 overflow-y-auto gap-8">
          <section className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
                Área de Construção
              </h3>
              <span className="text-2xs text-slate-500 italic">
                Arraste os blocos para as áreas pontilhadas
              </span>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {slots.map((slot) => (
                <div key={slot.type} className="flex items-center gap-4 group">
                  <div className="w-32 text-right">
                    <span className="text-2xs font-black text-slate-600 uppercase tracking-tighter">
                      {slot.label}
                    </span>
                  </div>
                  <div
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(slot.type)}
                    className={`flex-1 min-h-[80px] rounded-2xl flex items-center p-4 ${
                      slot.block
                        ? 'bg-accent-cyan/5 border-2 border-accent-cyan/30'
                        : 'border-2 border-dashed border-accent-cyan/20 hover:border-accent-cyan/50 hover:bg-accent-cyan/5'
                    } transition-all`}
                  >
                    {slot.block ? (
                      <div className="w-full bg-slate-800/80 p-4 rounded-xl border border-accent-cyan/30 flex items-center justify-between">
                        <span className="text-sm font-medium">{slot.block.content}</span>
                        <button
                          onClick={() => handleRemoveBlock(slot.type)}
                          className="text-slate-500 hover:text-red-500 transition-colors"
                        >
                          <span className="material-symbols-outlined text-lg">close</span>
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-700 text-sm font-medium italic w-full text-center">
                        Solte o bloco de {slot.type === 'persona' ? 'persona' : slot.type === 'context' ? 'contexto' : slot.type === 'task' ? 'tarefa' : 'restrições'} aqui
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-auto space-y-4">
            <div className="h-px bg-slate-800 w-full"></div>
            <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest">
              Biblioteca de Blocos
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {currentChallenge.availableBlocks.map((block) => (
                <div
                  key={block.id}
                  draggable={!usedBlocks.has(block.id)}
                  onDragStart={() => handleDragStart(block)}
                  className={`${
                    usedBlocks.has(block.id)
                      ? 'opacity-30 cursor-not-allowed'
                      : 'cursor-grab hover:scale-[1.02] active:scale-95'
                  } bg-slate-800/40 hover:bg-slate-800 p-4 rounded-xl border border-slate-700 flex items-center justify-between transition-all`}
                >
                  <span className="text-sm">{block.content}</span>
                  <span className="material-symbols-outlined text-slate-500 text-lg">
                    drag_indicator
                  </span>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-surface-primary border-t border-slate-800 px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2 text-slate-400">
            <span className="material-symbols-outlined">lightbulb</span>
            <span className="text-sm">{currentChallenge.hint}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleClearAll}
            className="px-6 py-3 rounded-xl font-bold text-slate-400 hover:text-content-primary transition-colors"
          >
            Limpar Tudo
          </button>
          <button
            onClick={handleTestPrompt}
            disabled={slots.some((slot) => !slot.block)}
            className="bg-accent hover:bg-blue-700 text-accent-contrast px-10 py-3 rounded-xl font-bold text-lg shadow-xl shadow-blue-900/20 transition-all active:scale-95 flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined">rocket_launch</span>
            Testar Prompt
          </button>
        </div>
      </footer>
    </div>
  );
};

export default PromptChallengePage;
